import { useEffect, useState } from 'react';
import Modal from '../Modal';
import { repositorioCultura, repositorioPlantio, repositorioSafra } from '../../sync/repositories/agricultura';
import { repositorioFazenda, repositorioTalhao } from '../../sync/repositories/fazendas';
import { extrairMensagensErro } from '../../utils/errorUtils';

const ESTADO_INICIAL = { fazenda: '', cultura: '', safra: '', talhao: '', area_hectares: '', data_plantio: '' };

/** Modal de cadastro rápido de Plantio. Busca fazenda/cultura/safra/talhão sozinho ao abrir. */
export default function ModalCadastroPlantio({ isOpen, onClose, onSuccess }) {
  const [dados, setDados] = useState(ESTADO_INICIAL);
  const [fazendas, setFazendas] = useState([]);
  const [culturas, setCulturas] = useState([]);
  const [safras, setSafras] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setDados(ESTADO_INICIAL);
      setErros([]);
      setSalvando(false);
      return;
    }
    repositorioFazenda.listar().then((r) => setFazendas(r.data.results || r.data)).catch(() => setFazendas([]));
    repositorioCultura.listar().then((r) => setCulturas(r.data.results || r.data)).catch(() => setCulturas([]));
    repositorioSafra.listar().then((r) => setSafras(r.data.results || r.data)).catch(() => setSafras([]));
    repositorioTalhao.listar().then((r) => setTalhoes(r.data.results || r.data)).catch(() => setTalhoes([]));
  }, [isOpen]);

  const handleChange = (campo) => (e) => setDados((prev) => ({ ...prev, [campo]: e.target.value }));

  const handleSalvar = async (e) => {
    e?.preventDefault?.();
    if (!dados.fazenda) { setErros(['Selecione a fazenda.']); return; }
    if (!dados.cultura) { setErros(['Selecione a cultura.']); return; }
    if (!dados.safra) { setErros(['Selecione a safra.']); return; }
    if (!dados.talhao) { setErros(['Selecione o talhão.']); return; }
    if (!dados.area_hectares) { setErros(['Informe a área plantada.']); return; }
    if (!dados.data_plantio) { setErros(['Informe a data do plantio.']); return; }
    setSalvando(true);
    setErros([]);
    try {
      const resp = await repositorioPlantio.criar({
        fazenda: dados.fazenda,
        cultura: dados.cultura,
        safra: dados.safra,
        talhao: dados.talhao,
        area_hectares: Number(dados.area_hectares),
        data_plantio: dados.data_plantio,
      });
      onSuccess?.(resp.data);
      onClose?.();
    } catch (error) {
      setErros(extrairMensagensErro(error));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={salvando ? () => {} : onClose} title="Cadastrar Plantio" size="lg">
      <form onSubmit={handleSalvar} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fazenda *</label>
            <select
              value={dados.fazenda}
              onChange={handleChange('fazenda')}
              disabled={salvando}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              <option value="">Selecione...</option>
              {fazendas.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Talhão *</label>
            <select
              value={dados.talhao}
              onChange={handleChange('talhao')}
              disabled={salvando}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              <option value="">Selecione...</option>
              {talhoes.map((t) => <option key={t.id} value={t.id}>{t.nome_codigo}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cultura *</label>
            <select
              value={dados.cultura}
              onChange={handleChange('cultura')}
              disabled={salvando}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              <option value="">Selecione...</option>
              {culturas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Safra *</label>
            <select
              value={dados.safra}
              onChange={handleChange('safra')}
              disabled={salvando}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              <option value="">Selecione...</option>
              {safras.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Área Plantada (ha) *</label>
            <input
              type="number" step="0.01" min="0.01"
              value={dados.area_hectares}
              onChange={handleChange('area_hectares')}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
              disabled={salvando}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data do Plantio *</label>
            <input
              type="date"
              value={dados.data_plantio}
              onChange={handleChange('data_plantio')}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
              disabled={salvando}
            />
          </div>
        </div>
        {erros.length > 0 && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 space-y-1">
            {erros.map((m, i) => <div key={i}>{m}</div>)}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={salvando}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50">
            Cancelar
          </button>
          <button type="submit" disabled={salvando}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
